import React, {useState, useRef, useEffect} from "react";
import {useObserver} from "mobx-react-lite";
import {useStore} from "../../../hooks/useStore";
import {useHistory} from "react-router-dom";
import {Col, Row, DatePicker} from "antd";
import {useTranslation} from "react-i18next";
import {
    RangePickerContainer,
    Title,
    SelectField,
    FilterContainer
} from "./courierStatisticsReportPage.styles";
import {timeRangeOptionsStatistics} from "../../../constants/constants";
import {removeNullEntries} from "../../../utils/helpers";
import {useFetch} from "../../../hooks/useFetch";
import {CourierStatisticsTable} from "./courierStatisticsTable/CourierStatisticsTable";
import {CityFilter, CompanyFilter} from "../../_defaultComponents/FilterComponents";

const {RangePicker} = DatePicker;

export const CourierStatisticsReportPage = () => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["courierStatisticsReportPage", "timeRangeOptionsStatistics"]);
    const [city, setCity] = useState("all");
    const [company, setCompany] = useState("all");
    const [subCompany, setSubCompany] = useState("all");
    const [startDateOrderStatistics, setStartDateOrderStatistics] = useState("");
    const [endDateOrderStatistics, setEndDateOrderStatistics] = useState("");
    const [dateTypeForOrderStatistics, setDateTypeForOrderStatistics] = useState("monthly");
    const [dependencyCondition, setDependencyCondition] = useState(true);

    const selectFieldOnChange = value => {
        setDependencyCondition(true);
        setStartDateOrderStatistics("");
        setEndDateOrderStatistics("");
        setDateTypeForOrderStatistics(value);
        if (value === "choose") {
            setDependencyCondition(false);
        }
    };
    const rangePickerOnChange = formatString => {
        setDependencyCondition(true);
        setStartDateOrderStatistics(formatString[0]);
        setEndDateOrderStatistics(formatString[1]);
        setDependencyCondition(true);
    };

    const [subCompaniesLoading, subCompanies] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            limit: 1000
        }),
        dependencies: [company],
        initialCall: false
    });

    const [courierDeliveryStatisticsLoading, courierDeliveryStatistics] = useFetch({
        fetchFn: stores.statisticStore.getCourierDeliveryStatistics.bind(
            null,
            removeNullEntries({
                period: dateTypeForOrderStatistics,
                startDate: startDateOrderStatistics,
                endDate: endDateOrderStatistics,
                companyId: company,
                subCompanyId: subCompany,
                city: city
            })
        ),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            company,
            subCompany,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    recallRef.current = recall;

    const courierStatisticLoading = false;

    const [currentPage, setCurrentPage] = useState(1);
    const [field, setField] = useState({field: "", asc: true});

    useEffect(() => {
        setSubCompany("all");
    }, [company]);

    useEffect(() => {
        setUrl();
    }, [currentPage, city, company, subCompany]);
    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        if (city !== "") {
            path += "&city=" + city;
        }
        if (company !== "") {
            path += "&company=" + company;
        }

        if (subCompany !== "") {
            path += "&subcompany=" + subCompany;
        }

        history.push(path);
    };

    return useObserver(() => (
        <Row type="flex" justify="start" data-testid="orderReportScreen">
            <Title data-testid="companyName">
                {t("courierStatisticsReportPageTitle", {ns: "courierStatisticsReportPage"})}
            </Title>
            <Col span={24} data-testid="filterCard">
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                        <CityFilter
                            value={city}
                            data-testid="citySelect"
                            onChange={value => {
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={8} md={8} lg={4} xl={4}>
                        <CompanyFilter
                            showAllOption={true}
                            showPoolOption={false}
                            allKey="all"
                            value={company}
                            onChange={value => setCompany(value)}
                        />
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                        <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                        <SelectField
                            data-testid="subCompanySelect"
                            value={subCompany}
                            onChange={value => {
                                setSubCompany(value);
                            }}
                            disabled={company === "all"}
                        >
                            <SelectField.Option value="all" data-testid="subCompanySelect_option">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {subCompanies.rows &&
                                subCompanies?.rows.map(subCompany => (
                                    <SelectField.Option
                                        key={subCompany._id}
                                        value={subCompany._id}
                                        data-testid="subCompanySelect_option"
                                    >
                                        {subCompany.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>

                    <Col xs={24} sm={8} md={8}>
                        <h5>{t("fields.date", {ns: "common"})}</h5>
                        <Row>
                            {rangePickerOnChange ? (
                                <Col span={8}>
                                    <SelectField
                                        data-testid="timeRangeSelect"
                                        value={dateTypeForOrderStatistics}
                                        onChange={value => {
                                            selectFieldOnChange(value);
                                        }}
                                    >
                                        {timeRangeOptionsStatistics.map((obj, i) => (
                                            <SelectField.Option
                                                key={i}
                                                data-testid="timeRangeSelect_option"
                                                value={obj.value}
                                            >
                                                {t(obj.translation_key, {
                                                    ns: "timeRangeOptionsStatistics"
                                                })}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </Col>
                            ) : null}
                            <Col span={16}>
                                {dateTypeForOrderStatistics === "choose" && (
                                    <RangePickerContainer>
                                        <RangePicker
                                            data-testid="timeRangePicker"
                                            renderExtraFooter={() => {
                                                t("timeRangePickerPlaceholder", {ns: "common"});
                                            }}
                                            placeholder={[
                                                t("fields.startDate", {ns: "common"}),
                                                t("fields.endDate", {ns: "common"})
                                            ]}
                                            onChange={(values, formatString, info) => {
                                                rangePickerOnChange(formatString);
                                            }}
                                        />
                                    </RangePickerContainer>
                                )}
                            </Col>
                        </Row>
                    </Col>
                </FilterContainer>
            </Col>
            <Col span={24} style={{marginTop: "40px"}}>
                <CourierStatisticsTable
                    loading={courierStatisticLoading}
                    data={courierDeliveryStatistics?.records}
                    dataCount={courierDeliveryStatistics?.records?.length}
                    currentPage={currentPage}
                    field={field}
                    setField={setField}
                    setPage={async page => {
                        setCurrentPage(page);
                        history.push("/reports/courierStatistics?page=" + page);
                    }}
                    setRecall={setRecall}
                    recallRef={recallRef}
                />
            </Col>
        </Row>
    ));
};
